import {create} from 'zustand';
import {User} from 'oidc-client-ts';
import {hasValue, updateAuthAxiosHeader} from '@kcast-fe/core';
import {hasAuthParams, setLoggerUser, UserManager} from 'core';

/* STORE ACTIONS */
type TStoreActions = {
  loadUser: () => Promise<void>;
  signOut: () => Promise<void>;
  _signIn: () => Promise<void>;
};

/* STORE FIELDS */
type TStoreState = {
  isFailed: boolean;
  isAuthenticated: boolean;
  user: User | null;
  actions: TStoreActions;
};

const INITIAL_STATE: Omit<TStoreState, 'actions'> = {
  user: null,
  isFailed: false,
  isAuthenticated: false
};

export const useAuthStore = create<TStoreState>()((set, get) => ({
  ...INITIAL_STATE,

  actions: {
    loadUser: async () => {
      try {
        await get().actions._signIn();
        const user = await UserManager.getUser();
        updateAuthAxiosHeader(user?.access_token ?? '');
        setLoggerUser(user?.profile.sub);
        set(() => ({user: user, isAuthenticated: hasValue(user)}));
      } catch (error) {
        set(() => ({isFailed: true}));
        console.error('[Auth] Error:', error);
      }
    },

    _signIn: async () => {
      if (hasAuthParams()) {
        console.info('[Auth] Url has auth params.');
        try {
          await UserManager.signinCallback();
        } catch (error) {
          console.error('[Auth] Error:', error);
        }

        window.history.replaceState({}, document.title, window.location.pathname);
      }

      UserManager.events.addSilentRenewError((error) => {
        console.info('[Auth] Silent renew error:', error);
        return UserManager.signinRedirect();
      });

      UserManager.events.addUserLoaded((user) => {
        console.info(`[Auth] Token renewed at ${new Date().toString()}.`);
        updateAuthAxiosHeader(user?.access_token ?? '');
      });

      let oidcUser = await UserManager.getUser();

      if (!oidcUser || oidcUser.expired) {
        console.info('[Auth] User is empty or expired, refreshing...');
        try {
          oidcUser = await UserManager.signinSilent();
        } catch (error) {
          console.info('[Auth] signinSilent error: ', error, '.');
        }
      }

      if (!oidcUser) {
        console.info('[Auth] User is empty, redirect to sign in.');
        window.localStorage.clear();
        await UserManager.signinRedirect();
        return;
      }

      console.info(`[Auth] ${oidcUser.profile?.name}.`);
    },

    signOut: async () => {
      await UserManager.signoutRedirect();
      setLoggerUser();
      set(() => ({user: null, isAuthenticated: false}));
    }
  }
}));
